import React, { useState } from 'react';
import { Send } from 'lucide-react';
import { Button } from './ui/button';
import { trackButtonClick } from '../utils/analytics';
import { submitFormToLambda } from '../utils/api';
import { toast } from 'sonner';

const BundleInquiryForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    mobile: '',
    email: '',
    software: '',
    users: '',
    mode: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    trackButtonClick('bundle_inquiry_submit', 'services_bundle_form');
    
    try {
      // Submit to Lambda API
      await submitFormToLambda('bundle_inquiry', {
        name: formData.name,
        company: formData.company,
        mobile: formData.mobile,
        email: formData.email,
        software: formData.software,
        users: formData.users,
        mode: formData.mode
      });

      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setFormData({
          name: '',
          company: '',
          mobile: '',
          email: '',
          software: '',
          users: '',
          mode: ''
        });
      }, 3000);
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error(error.message || 'Failed to submit inquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-white to-[#F5F7FA] rounded-3xl p-8 shadow-2xl border-2 border-[#0056D2]/20">
      <div className="text-center mb-8">
        <h3 className="text-2xl md:text-3xl font-bold text-[#0A1F44] mb-3">
          Get Started with Bundle Deals
        </h3>
        <p className="text-gray-600">
          Fill in your details and our team will contact you shortly
        </p>
      </div>

      {submitted ? (
        <div className="text-center py-12">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>
          <h4 className="text-2xl font-bold text-green-600 mb-2">Thank You!</h4>
          <p className="text-gray-600">We'll get back to you soon.</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Name */}
            <div>
              <label className="block text-sm font-semibold text-[#0A1F44] mb-2">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#0056D2] focus:outline-none transition-colors"
                placeholder="Your full name"
              />
            </div>

            {/* Company */}
            <div>
              <label className="block text-sm font-semibold text-[#0A1F44] mb-2">
                Company <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#0056D2] focus:outline-none transition-colors"
                placeholder="Company name"
              />
            </div>

            {/* Mobile */}
            <div>
              <label className="block text-sm font-semibold text-[#0A1F44] mb-2">
                Mobile <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                required
                pattern="[0-9]{10}"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#0056D2] focus:outline-none transition-colors"
                placeholder="10-digit mobile number"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-semibold text-[#0A1F44] mb-2">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#0056D2] focus:outline-none transition-colors"
                placeholder="your.email@company.com"
              />
            </div>

            {/* Software */}
            <div>
              <label className="block text-sm font-semibold text-[#0A1F44] mb-2">
                Software <span className="text-red-500">*</span>
              </label>
              <select
                name="software"
                value={formData.software}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#0056D2] focus:outline-none transition-colors"
              >
                <option value="">Select software</option>
                <option value="Tally">Tally</option>
                <option value="Marg">Marg</option>
                <option value="Busy">Busy</option>
                <option value="Custom">Custom</option>
              </select>
            </div>

            {/* Number of Users */}
            <div>
              <label className="block text-sm font-semibold text-[#0A1F44] mb-2">
                Number of Users <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="users"
                value={formData.users}
                onChange={handleChange}
                required
                min="1"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#0056D2] focus:outline-none transition-colors"
                placeholder="e.g., 5"
              />
            </div>

            {/* Current Software Usage Mode */}
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-[#0A1F44] mb-2">
                Current Software Usage Mode <span className="text-red-500">*</span>
              </label>
              <div className="flex space-x-6">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="mode"
                    value="Cloud"
                    checked={formData.mode === 'Cloud'}
                    onChange={handleChange}
                    required
                    className="w-4 h-4 text-[#0056D2] focus:ring-[#0056D2]"
                  />
                  <span className="text-gray-700">Cloud</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="mode"
                    value="Local"
                    checked={formData.mode === 'Local'}
                    onChange={handleChange}
                    required
                    className="w-4 h-4 text-[#0056D2] focus:ring-[#0056D2]"
                  />
                  <span className="text-gray-700">Local</span>
                </label>
              </div>
            </div>
          </div>

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-gradient-to-r from-[#0056D2] to-[#0A1F44] hover:from-[#0A1F44] hover:to-[#0056D2] text-white font-bold py-4 rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-xl text-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
            <Send className="ml-3" size={20} />
          </Button>
        </form>
      )}
    </div>
  );
};

export default BundleInquiryForm;
